// ./src/components/features/Categories/CategoriesInput/CategoriesInput.tsx
import React, { useMemo, useCallback } from "react";
import { TagsInput, TagsInputProps } from "@mantine/core";
import { useCategoriesContext } from "@/contexts/CategoriesContext";
import { LoadingIndicator } from "@/components/generic/LoadingIndicator";

export interface CategoriesInputProps
  extends Omit<TagsInputProps, "data" | "value" | "onChange"> {
  /** Selected category names */
  value?: string[] | null | undefined;

  /** Callback when category selection changes - receives array of category names */
  onChange?: (categoryNames: string[]) => void;

  /** Placeholder text when no categories are selected */
  placeholder?: string;

  /** Custom filter function for categories */
  categoryFilter?: (category: any) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Loading placeholder text */
  loadingPlaceholder?: string;

  /** Empty state text when no categories are available */
  emptyText?: string;

  /** Maximum number of categories that can be selected */
  maxValues?: number;
}

export const CategoriesInput: React.FC<CategoriesInputProps> = ({
  value,
  onChange,
  placeholder = "Select or create categories",
  categoryFilter,
  required = false,
  loadingPlaceholder = "Loading categories...",
  emptyText = "Start typing to create a category",
  maxValues,
  error,
  disabled,
  size = "md",
  ...tagsInputProps
}) => {
  const { categories, loading, isEmpty, lastError } = useCategoriesContext();

  // Filter and prepare category options (suggestions)
  const categoryOptions = useMemo(() => {
    let filteredCategories = categories;

    // Apply custom filter if provided
    if (categoryFilter) {
      filteredCategories = filteredCategories.filter(categoryFilter);
    }

    // Convert to array of category names for suggestions
    return filteredCategories
      .filter((category) => category.name) // Ensure category has a name
      .map((category) => category.name!);
  }, [categories, categoryFilter]);

  // Handle onChange
  const handleChange = useCallback(
    (selectedCategoryNames: string[]) => {
      onChange?.(selectedCategoryNames);
    },
    [onChange]
  );

  // Determine if max values reached
  const isMaxReached = useMemo(() => {
    if (!maxValues) return false;
    return (value?.length || 0) >= maxValues;
  }, [maxValues, value]);

  // Determine the current state and configure tags input props accordingly
  const getTagsInputState = () => {
    // Loading state
    if (loading && categories.length === 0) {
      return {
        data: [],
        placeholder: loadingPlaceholder,
        disabled: true,
        rightSection: <LoadingIndicator size={16} color="green" />,
      };
    }

    // Error state
    if (lastError && !loading) {
      return {
        data: [],
        placeholder: "Error loading categories",
        disabled: true,
        error: error || "Failed to load categories",
      };
    }

    // Empty state (suggestions will be empty but user can still type)
    if (isEmpty && !loading) {
      return {
        data: [],
        placeholder: emptyText,
        disabled: disabled || isMaxReached,
        rightSection: undefined,
      };
    }

    // Normal state with data
    return {
      data: categoryOptions,
      placeholder: isMaxReached
        ? `Maximum ${maxValues} categories selected`
        : placeholder,
      disabled: disabled || loading || isMaxReached,
      rightSection:
        loading && categories.length > 0 ? (
          <LoadingIndicator size={16} color="green" />
        ) : (
          tagsInputProps.rightSection
        ),
    };
  };

  const tagsInputState = getTagsInputState();

  // Render the tags input component
  return (
    <TagsInput
      {...tagsInputProps}
      size={size}
      value={value || []} // Convert null/undefined to empty array
      onChange={handleChange}
      required={required}
      clearable={!required}
      maxTags={maxValues}
      error={tagsInputState.error || error}
      data={tagsInputState.data}
      placeholder={tagsInputState.placeholder}
      disabled={tagsInputState.disabled}
      rightSection={tagsInputState.rightSection}
    />
  );
};
